import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
   subscribeSubPopup,
   getCurrentSubPopup,
   closeSubPopup as requestCloseSubPopup,
   popSubPopup,
} from "./popupUIStore";
import { triggerHapticFeedback } from "../../utils/hapticFeedback";
import { ButtonUI } from "../UI";
import { PopupActionsContext } from "./PopupActions";
import ReservationEditPopup from "../Popups/EditReservation/EditReservation";

// pe telefon sub-popup-ul e o foaie de jos (scss/_popup.scss): la închidere așteptăm să coboare;
// = $mobile din App.scss și durata din --popupUI-sheet-out
const SHEET_MEDIA = "(max-width: 767.98px)";
const SHEET_CLOSE_MS = 360;

function shouldAnimateSheetClose() {
   if (typeof window === "undefined" || !window.matchMedia) return false;
   return (
      window.matchMedia(SHEET_MEDIA).matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
   );
}

function resolveSubPopupPortalHost() {
   if (typeof document === "undefined") return null;
   const doc = document;
   const fullscreenEl = doc.fullscreenElement || doc.webkitFullscreenElement;
   if (
      !fullscreenEl ||
      fullscreenEl === doc.documentElement ||
      fullscreenEl === doc.body
   ) {
      return doc.body;
   }
   return fullscreenEl;
}

export default function SubPopupUI() {
   const [portalHost, setPortalHost] = useState(() =>
      resolveSubPopupPortalHost(),
   );
   const [popupState, setPopupState] = useState({
      type: getCurrentSubPopup()?.type || null,
      props: getCurrentSubPopup()?.props || {},
   });
   const [exiting, setExiting] = useState(false);
   const [armed, setArmed] = useState(false);
   const panelRef = useRef(null);

   // cheie care forteaza remount la fiecare OPEN de subpopup
   const [openKey, setOpenKey] = useState(0);

   const isMobile = () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 860px)").matches;

   // starea SubPopup in history
   const subSessionIdRef = useRef(0);
   const subDepthRef = useRef(0);

   // back initiat de gest (popstate) ca sa nu mai dam inca un history.back() dupa animatie
   const closingByPopstateRef = useRef(false);

   // cand inchidem top-ul si reapare precedentul (nu mai push-uim history)
   const revealingPrevRef = useRef(false);

   const handleClose = () => {
      triggerHapticFeedback();
      requestCloseSubPopup();
   };

   useEffect(() => {
      if (typeof document === "undefined") return undefined;

      const syncPortalHost = () => {
         setPortalHost(resolveSubPopupPortalHost());
      };

      syncPortalHost();
      document.addEventListener("fullscreenchange", syncPortalHost);
      document.addEventListener("webkitfullscreenchange", syncPortalHost);

      return () => {
         document.removeEventListener("fullscreenchange", syncPortalHost);
         document.removeEventListener("webkitfullscreenchange", syncPortalHost);
      };
   }, []);

   // Back/gest — daca exista un SubPopup deschis, il inchidem noi (nu Popup)
   useEffect(() => {
      const onPopState = () => {
         if (getCurrentSubPopup()) {
            closingByPopstateRef.current = true;
            requestCloseSubPopup();
            return;
         }
      };
      window.addEventListener("popstate", onPopState);
      return () => window.removeEventListener("popstate", onPopState);
   }, []);

   useEffect(() => {
      let sheetCloseTimer = 0;

      const closeNow = () => {
         if (!getCurrentSubPopup()) {
            closingByPopstateRef.current = false;
            return;
         }

         // CLOSE REQUEST
         setExiting(false);
         setArmed(false);
         document.body.classList.remove("subpopup-open");

         // scoatem top-ul din stiva imediat
         revealingPrevRef.current = true;
         popSubPopup();

         // pe mobil: consumam o intrare din istorie DOAR daca inchiderea nu a venit din popstate
         if (isMobile() && subDepthRef.current > 0) {
            if (!closingByPopstateRef.current) {
               window.history.back();
            }
            subDepthRef.current -= 1;
         }
         closingByPopstateRef.current = false;
      };

      const handleRequestClose = () => {
         if (!getCurrentSubPopup()) {
            closingByPopstateRef.current = false;
            return;
         }

         // foaia de pe telefon coboară întâi (fără .is-armed, iar voalul se stinge), apoi se închide
         if (sheetCloseTimer) return;
         if (shouldAnimateSheetClose()) {
            setArmed(false);
            document.body.classList.remove("subpopup-open");
            sheetCloseTimer = window.setTimeout(() => {
               sheetCloseTimer = 0;
               closeNow();
            }, SHEET_CLOSE_MS);
            return;
         }
         closeNow();
      };

      const unsub = subscribeSubPopup(({ detail, action }) => {
         if (action === "request-close") {
            handleRequestClose();
            return;
         }

         if (detail) {
            // OPEN / REVEAL (deschis cât cobora foaia: rămâne deschis, nu-l mai închidem)
            if (sheetCloseTimer) {
               window.clearTimeout(sheetCloseTimer);
               sheetCloseTimer = 0;
               closingByPopstateRef.current = false;
            }
            setExiting(false);
            setArmed(false);
            document.body.classList.add("subpopup-open");
            setPopupState({
               type: detail.type,
               props: { ...(detail.props || {}) },
            });

            // forteaza remount doar cand deschidem ceva nou (nu cand reapare precedentul)
            if (!revealingPrevRef.current) {
               subSessionIdRef.current += 1;
               setOpenKey(subSessionIdRef.current);
            }

            requestAnimationFrame(() =>
               requestAnimationFrame(() => setArmed(true)),
            );

            // fiecare SubPopup deschis impinge o intrare separata in istorie (stack);
            // inlocuirea (acelasi tip, alt continut) ramane pe aceeasi intrare
            if (isMobile() && !revealingPrevRef.current && action !== "replace") {
               const sid = subSessionIdRef.current;
               window.history.pushState(
                  { __subpopup_ui_dummy: true, sid },
                  "",
                  window.location.href,
               );
               subDepthRef.current += 1;
            }
            revealingPrevRef.current = false;
            return;
         }

         // STACK EMPTY
         setExiting(false);
         setArmed(false);
         document.body.classList.remove("subpopup-open");
         setPopupState({ type: null, props: {} });
         revealingPrevRef.current = false;
      });

      return () => {
         window.clearTimeout(sheetCloseTimer);
         document.body.classList.remove("subpopup-open");
         unsub();
      };
   }, []);

   // rândul de sus: lățimea lui ajunge în --popupUI-bar-w (titlul își lasă loc)
   const [actionsHost, setActionsHost] = useState(null);
   const barRowRef = useRef(null);
   const hasSub = Boolean(popupState.type);
   useEffect(() => {
      const row = barRowRef.current;
      const panel = panelRef.current;
      if (!row || !panel || typeof ResizeObserver === "undefined") return undefined;
      const sync = () => {
         panel.style.setProperty("--popupUI-bar-w", `${Math.ceil(row.getBoundingClientRect().width) + 24}px`);
      };
      sync();
      const ro = new ResizeObserver(sync);
      ro.observe(row);
      return () => ro.disconnect();
   }, [hasSub]);

   const renderContent = () => {
      switch (popupState.type) {
         case "reservationEdit":
            return (
               <ReservationEditPopup
                  key={openKey}
                  onClose={handleClose}
                  {...popupState.props}
               />
            );
         default: {
            const Cmp = popupState.props?.component;
            return Cmp ? (
               <Cmp key={openKey} onClose={handleClose} {...popupState.props} />
            ) : null;
         }
      }
   };

   if (!popupState.type) return null;

   const tree = (
      <>
         <div
            className={`subpopup-panel__overlay ${armed ? "is-armed" : ""}`}
            onClick={handleClose}
         />
         <div
            className={`subpopup-panel ${armed ? "is-armed" : ""} ${exiting ? "subpopup-exit" : ""}`}
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
         >
            <div className="subpopup-panel__inner">
               {/* același rând de sus ca la PopupUI: <PopupActions> + „Închide” */}
               <div className="popupPanelUI__bar">
                  <div ref={barRowRef} className="popupPanelUI__barRow">
                     <div ref={setActionsHost} className="popupPanelUI__actions" />
                     <ButtonUI
                        icon="close"
                        aria-label="Închide"
                        title="Închide"
                        className="popupPanelUI__close"
                        onClick={() => handleClose()}
                     />
                  </div>
               </div>
               <div className="subpopup-panel__content">
                  <PopupActionsContext.Provider value={actionsHost}>
                     {renderContent()}
                  </PopupActionsContext.Provider>
               </div>
            </div>
         </div>
      </>
   );

   return portalHost ? createPortal(tree, portalHost) : tree;
}
